import React, { useEffect, useRef, useState } from 'react';
import { isAndroid, isIOS } from 'react-device-detect';

import { useUILayerStore } from '@_shared/stores/useUILayerStore';
import { InformationPopup } from '@_shared/ui/Popup';

import { BackHeader } from '../BackHeader';
import { BottomNavigationBar } from './BottomNavigationBar';
import * as S from './PageTemplate.style';

interface Props {
	children: React.ReactNode;
	nav: boolean | React.ReactNode;
	header: boolean | React.ReactNode;
}

export function PageTemplate({ children, nav, header }: Props) {
	const { layers } = useUILayerStore();
	const [bottomHeight, setBottomHeight] = useState(0);

	const bottomRef = useRef(null);

	// 업데이트 안내 팝업 - 닫기 기능
	const [isUpdatePopupOpenTime, setIsUpdatePopupOpenTime] = useState(
		localStorage.getItem('isUpdatePopupOpenTime')
			? new Date(localStorage.getItem('isUpdatePopupOpenTime')!) < new Date()
			: window.location.href.includes('gabozago.kr'),
	);
	const [pressTimer, setPressTimer] = useState<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (!bottomRef.current) return () => {};
		const resizeObserver = new ResizeObserver((entries) => {
			setBottomHeight(entries[0].target.clientHeight);
		});
		resizeObserver.observe(bottomRef.current);

		return () => resizeObserver.disconnect();
	}, [bottomRef.current]);

	useEffect(() => {
		// layers 배열이 비어있는 경우 처리
		if (layers.length === 0) {
			document.body.style.overflow = 'auto';
			return;
		}

		if (
			layers[layers.length - 1].type === 'modal' ||
			layers[layers.length - 1].type === 'popup'
		) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'auto';
		}
	}, [layers]);

	return (
		<S.Container>
			{/* 업데이트 안내 팝업 */}
			{isUpdatePopupOpenTime && (
				<S.EmergencyPopup>
					<InformationPopup.Container closePopup={() => {}}>
						<InformationPopup.TextContainer>
							<InformationPopup.Title>🚨 앱 업데이트 안내</InformationPopup.Title>
							<InformationPopup.SubText>
								안녕하세요.포레 입니다.
								<br />
								폴더명 변경 기능 및 앱 안정화를 위한
								<br />
								새로운 앱 버전이 업데이트 되었습니다.
								<br />
								해당 버전은 곧 지원 중단될 예정이오니
								<br />앱 업데이트를 부탁드립니다 🙇🏻‍♀️
							</InformationPopup.SubText>
						</InformationPopup.TextContainer>
						<InformationPopup.ButtonContainer>
							<div
								style={{
									width: '100%',
									height: '100%',
								}}
								onTouchStart={() => {
									const timer = setTimeout(() => {
										const currentTime = new Date();
										currentTime.setHours(currentTime.getHours() + 6);

										localStorage.setItem(
											'isUpdatePopupOpenTime',
											currentTime.toISOString(),
										);
										setIsUpdatePopupOpenTime(false);
									}, 5000);

									setPressTimer(timer);
								}}
								onTouchEnd={() => {
									if (pressTimer) {
										clearTimeout(pressTimer);
										setPressTimer(null);
									}
								}}
								onTouchMove={() => {
									if (pressTimer) {
										clearTimeout(pressTimer);
										setPressTimer(null);
									}
								}}
							>
								<InformationPopup.Button
									type="confirm"
									onClick={() => {
										if (isAndroid) {
											window.location.href =
												'https://play.google.com/store/apps/details?id=com.teamfore.gabozago';
										}
										if (isIOS) {
											window.location.href =
												'https://apps.apple.com/kr/app/딸깍-콘텐츠-링크-아카이빙-앱/id6670229348';
										}
									}}
								>
									앱 업데이트
								</InformationPopup.Button>
							</div>
						</InformationPopup.ButtonContainer>
					</InformationPopup.Container>
				</S.EmergencyPopup>
			)}

			<S.Header>
				{typeof header !== 'boolean' && header}
				{header === true && <BackHeader />}
			</S.Header>

			<S.Content bottomHeight={bottomHeight}>{children}</S.Content>

			<S.Bottom ref={bottomRef}>
				{typeof nav !== 'boolean' && nav}
				{nav === true && <BottomNavigationBar />}
			</S.Bottom>

			{layers.map((layer) => (
				<layer.UI key={layer.id} />
			))}
		</S.Container>
	);
}
